import React, { useEffect } from 'react'
import './Modal.css'
import { translations, type Language, type Translations } from './i18n'

export interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  message: string
  details?: string
  type?: 'error' | 'warning' | 'success' | 'info'
  primaryButtonText?: string
  secondaryButtonText?: string
  onPrimaryClick?: () => void
  onSecondaryClick?: () => void
  language?: Language
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  details,
  type = 'info',
  primaryButtonText,
  secondaryButtonText,
  onPrimaryClick,
  onSecondaryClick,
  language = 'zh',
}) => {
  const t = translations[language]
  const defaultPrimaryText = primaryButtonText || t.modalConfirm
  // ESC 键关闭
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [isOpen, onClose])

  // 阻止背景滚动
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  if (!isOpen) return null

  const getIcon = () => {
    switch (type) {
      case 'error':
        return '❌'
      case 'warning':
        return '⚠️'
      case 'success':
        return '✅'
      case 'info':
      default:
        return 'ℹ️'
    }
  }

  const handlePrimary = () => {
    if (onPrimaryClick) {
      onPrimaryClick()
    }
    onClose()
  }

  const handleSecondary = () => {
    if (onSecondaryClick) {
      onSecondaryClick()
    }
    onClose()
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className={`modal-icon ${type}`}>{getIcon()}</span>
          <h3 className="modal-title">{title}</h3>
        </div>

        <div className="modal-body">
          <div className="modal-message">{message}</div>
          {details && (
            <div className="modal-details">
              <strong>{t.modalDetails}</strong>
              <br />
              {details}
            </div>
          )}
        </div>

        <div className="modal-footer">
          {secondaryButtonText && (
            <button
              className="modal-button modal-button-secondary"
              onClick={handleSecondary}
            >
              {secondaryButtonText}
            </button>
          )}
          <button
            className="modal-button modal-button-primary"
            onClick={handlePrimary}
          >
            {defaultPrimaryText}
          </button>
        </div>
      </div>
    </div>
  )
}

// 错误消息友好化工具函数
export const friendlyErrorMessage = (error: any, language: Language = 'zh'): { title: string; message: string; details?: string } => {
  const t = translations[language]
  const errorMessage = error?.message || String(error)

  // 用户拒绝交易
  if (errorMessage.includes('user rejected') || errorMessage.includes('user denied') || error?.code === 4001) {
    return {
      title: t.errorTxCancelled,
      message: t.errorTxCancelledMsg,
      details: undefined,
    }
  }

  // 余额不足
  if (errorMessage.includes('insufficient funds') || errorMessage.includes('insufficient balance')) {
    return {
      title: t.errorInsufficientFunds,
      message: t.errorInsufficientFundsMsg,
      details: errorMessage,
    }
  }

  // Gas 不足
  if (errorMessage.includes('gas') && errorMessage.includes('insufficient')) {
    return {
      title: t.errorGasInsufficient,
      message: t.errorGasInsufficientMsg,
      details: errorMessage,
    }
  }

  // 未授权
  if (errorMessage.includes('allowance') || errorMessage.includes('not approved')) {
    return {
      title: t.errorNotApproved,
      message: t.errorNotApprovedMsg,
      details: errorMessage,
    }
  }

  // Commitment 已使用
  if (errorMessage.includes('commitment already used') || errorMessage.includes('Commitment already exists')) {
    return {
      title: t.errorCommitmentUsed,
      message: t.errorCommitmentUsedMsg,
      details: undefined,
    }
  }

  // Nullifier 已使用
  if (errorMessage.includes('nullifier') && errorMessage.includes('used')) {
    return {
      title: t.errorNullifierUsed,
      message: t.errorNullifierUsedMsg,
      details: undefined,
    }
  }

  // 时间延迟
  if (errorMessage.includes('too early') || errorMessage.includes('minimum delay')) {
    return {
      title: t.errorTooEarly,
      message: t.errorTooEarlyMsg,
      details: undefined,
    }
  }

  // 网络错误
  if (errorMessage.includes('network') || errorMessage.includes('connection')) {
    return {
      title: t.errorNetwork,
      message: t.errorNetworkMsg,
      details: errorMessage,
    }
  }

  // 合约执行失败
  if (errorMessage.includes('execution reverted') || errorMessage.includes('revert')) {
    return {
      title: t.errorTxFailed,
      message: t.errorTxFailedMsg,
      details: errorMessage,
    }
  }

  // 金额错误
  if (errorMessage.includes('amount') || errorMessage.includes('invalid')) {
    return {
      title: t.errorInvalidAmountTitle,
      message: t.errorInvalidAmountMsg,
      details: errorMessage,
    }
  }

  // 默认错误
  return {
    title: t.errorDefault,
    message: t.errorDefaultMsg,
    details: errorMessage,
  }
}
